import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getWebSocketClient } from '../../../lib/websocket/boardWebSocket';
import { useAuthStore } from '../../../stores/authStore';
import { useBoardStore } from '../../../stores/boardStore';
import { useUserChannelsStore } from '../../../stores/userChannelsStore';

/**
 * Hook to manage WebSocket connection for a board
 * Automatically subscribes/unsubscribes and handles cache invalidation
 */
export function useBoardWebSocket(boardId: string | null) {
  const queryClient = useQueryClient();
  const token = useAuthStore(state => state.tokens?.accessToken);
  const refreshTokens = useAuthStore(state => state.refreshTokens);

  /**
   * Token refresh function for WebSocket client
   */
  const getFreshToken = async (): Promise<string> => {
    try {
      const newTokens = await refreshTokens();
      return newTokens.accessToken;
    } catch (error) {
      console.error('[useBoardWebSocket] Failed to refresh token:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (!boardId || !token) {
      return;
    }

    let wsClient: ReturnType<typeof getWebSocketClient> | null = null;

    try {
      wsClient = getWebSocketClient(token, getFreshToken);

      // Connect and subscribe
      wsClient.connect().then(() => {
        if (wsClient && boardId) {
          wsClient.subscribeToBoard(boardId);
        }
      }).catch(error => {
        console.error('[useBoardWebSocket] Connection failed:', error);
      });

      // Handle channel synced event
      // Note: channel:synced events from EntitySyncService are user-scoped (broadcastToUser)
      // and may not include boardId. We check if the synced channel belongs to the current board.
      const handleChannelSynced = (data: any) => {
        const boardChannels = useBoardStore.getState().channels;
        const isRelevant = data.boardId === boardId ||
          boardChannels.some(ch => String(ch.id) === String(data.channelId));

        if (isRelevant) {
          console.log('[useBoardWebSocket] Channel synced:', data.channelId);

          // Update user channels store
          const store = useUserChannelsStore.getState();
          store.updateChannel(data.channelId, {
            status: (data.status && data.status !== 'syncing') ? data.status : 'active',
            lastSyncAt: data.lastSyncAt || new Date().toISOString()
          });
          store.setSyncStatusMessage(data.channelId, "");

          // Invalidate the unified board payload query with a slight delay
          // to ensure the backend has finished its work
          setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: ['board-payload', boardId] });
          }, 500);
        }
      };

      // Handle channel sync status event
      const handleChannelSyncStatus = (data: any) => {
        if (data.channelId && data.message) {
          const store = useUserChannelsStore.getState();
          // Also set the channel status to syncing if it isn't already to ensure UI feedback
          store.updateChannel(data.channelId, { status: 'syncing' });
          store.setSyncStatusMessage(data.channelId, data.message);
        }
      };

      // Handle channel sync failed event
      const handleChannelSyncFailed = (data: any) => {
        console.log('[useBoardWebSocket] Channel sync failed:', data.channelId);
        const store = useUserChannelsStore.getState();
        store.updateChannel(data.channelId, {
          status: data.status || 'error'
        });
        store.setSyncStatusMessage(data.channelId, "");

        // Invalidate with delay
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['board-payload', boardId] });
        }, 500);
      };

      // Handle board updated event
      const handleBoardUpdated = (data: any) => {
        if (data.boardId === boardId) {
          console.log('[useBoardWebSocket] Board updated:', data.changes.type);

          // Check which fields were updated
          const updatedFields = data.changes.updatedFields || [];
          const isMetadataUpdateOnly = updatedFields.every((field: string) =>
            ['name', 'description'].includes(field)
          );

          if (data.changes.type === 'journal_data') {
            console.log('[useBoardWebSocket] Journal data updated - triggering full invalidation');
            queryClient.invalidateQueries({ queryKey: ['board-payload', boardId] });
            queryClient.invalidateQueries({ queryKey: ['journal-operations'] });
            return;
          }

          if (isMetadataUpdateOnly) {
            console.log('[useBoardWebSocket] Metadata update only (name/description) - skipping finance invalidation');
            // Still invalidate the unified payload as it contains metadata
            queryClient.invalidateQueries({ queryKey: ['board-payload', boardId] });
            // Also invalidate the main board query to update the title in the list/header if needed
            queryClient.invalidateQueries({ queryKey: ['board', boardId] });
          } else {
            // Invalidate the unified board payload query
            queryClient.invalidateQueries({ queryKey: ['board-payload', boardId] });
          }

          // If currency changed, also invalidate analytics
          if (data.changes.type === 'currency_change' || (updatedFields.includes('currency') || updatedFields.includes('boardExchangeRates'))) {
            queryClient.invalidateQueries({
              queryKey: ['board-analytics', boardId]
            });
          }
        }
      };

      // Handle entity selection update event
      const handleEntitiesUpdated = (data: any) => {
        if (data.boardId === boardId) {
          console.log('[useBoardWebSocket] Entities updated:', data.selectedEntityIds.length, 'entities');

          // Get current user ID to check if this update came from us
          const currentUserId = useAuthStore.getState().user?.id;

          // Skip invalidation if this update was triggered by the current user
          // The validation handler already updated the cache with setQueryData
          if (data.updatedBy && data.updatedBy === currentUserId) {
            console.log('[useBoardWebSocket] Skipping invalidation - update from current user');
            return;
          }

          // Invalidate the unified board payload query
          queryClient.invalidateQueries({ queryKey: ['board-payload', boardId] });
        }
      };

      // Handle date range update event
      const handleDateRangeUpdated = (data: any) => {
        if (data.boardId === boardId) {
          console.log('[useBoardWebSocket] Date range updated:', data.dateRange);

          // Invalidate the unified board payload query
          queryClient.invalidateQueries({ queryKey: ['board-payload', boardId] });
          queryClient.invalidateQueries({ queryKey: ['board-analytics', boardId] });
        }
      };

      // Handle currency update event
      const handleCurrencyUpdated = (data: any) => {
        if (data.boardId === boardId) {
          console.log('[useBoardWebSocket] Currency updated:', data.currencySettings);

          // Invalidate the unified board payload query
          queryClient.invalidateQueries({ queryKey: ['board-payload', boardId] });
          queryClient.invalidateQueries({ queryKey: ['board-analytics', boardId] });
        }
      };

      // Handle sync progress event
      const handleSyncProgress = (data: any) => {
        console.log('[useBoardWebSocket] Sync progress:', data);
        // Could update UI state here if needed
      };

      // Handle error event
      const handleError = (data: any) => {
        console.error('[useBoardWebSocket] Error:', data);
      };

      // Handle channel connected event
      const handleChannelConnected = (data: any) => {
        console.log('[useBoardWebSocket] Channel connected:', data.channelId);
        queryClient.invalidateQueries({ queryKey: ['board-payload', boardId] });
      };

      // Handle channel disconnected event
      const handleChannelDisconnected = (data: any) => {
        console.log('[useBoardWebSocket] Channel disconnected:', data.channelId);
        queryClient.invalidateQueries({ queryKey: ['board-payload', boardId] });
      };

      // Register event handlers
      wsClient.on('channel:synced', handleChannelSynced);
      wsClient.on('channel:sync_status', handleChannelSyncStatus);
      wsClient.on('channel:sync_failed', handleChannelSyncFailed);
      wsClient.on('board:updated', handleBoardUpdated);
      wsClient.on('board:entities_updated', handleEntitiesUpdated);
      wsClient.on('board:daterange_updated', handleDateRangeUpdated);
      wsClient.on('board:currency_updated', handleCurrencyUpdated);
      wsClient.on('channel:sync_progress', handleSyncProgress);
      wsClient.on('error', handleError);
      wsClient.on('channel:connected', handleChannelConnected);
      wsClient.on('channel:disconnected', handleChannelDisconnected);

      // Cleanup
      return () => {
        if (wsClient && boardId) {
          wsClient.unsubscribeFromBoard(boardId);
          wsClient.off('channel:synced', handleChannelSynced);
          wsClient.off('channel:sync_status', handleChannelSyncStatus);
          wsClient.off('channel:sync_failed', handleChannelSyncFailed);
          wsClient.off('board:updated', handleBoardUpdated);
          wsClient.off('board:entities_updated', handleEntitiesUpdated);
          wsClient.off('board:daterange_updated', handleDateRangeUpdated);
          wsClient.off('board:currency_updated', handleCurrencyUpdated);
          wsClient.off('channel:sync_progress', handleSyncProgress);
          wsClient.off('error', handleError);
          wsClient.off('channel:connected', handleChannelConnected);
          wsClient.off('channel:disconnected', handleChannelDisconnected);
        }
      };
    } catch (error) {
      console.error('[useBoardWebSocket] Failed to initialize:', error);
    }
  }, [boardId, token, queryClient]);
}
